import React from 'react';
import styled from 'styled-components';
import { getCssSizeValue, getFontSize } from '@repo/utils';
import { BREAKPOINTS } from '@repo/ui/themeSettings';
import { CURSOR_TYPES, ICONS } from '@repo/utils/constants';
import { useRouter } from 'next/router';
import { underlineAnimationColorCss } from '@/components/Menu';
import AuthorInfo from '@/components/allNewsPresentation/AuthorInfo';
import DateInfo from '@repo/ui/src/components/common/DateInfo';
import Image, { generateSizes } from '@repo/ui/src/components/common/Image';
import Icon from '@repo/ui/src/components/common/Icon/Icon';
import Link from '@repo/ui/src/components/common/Link';

const SingleNewsBannerContainer = styled.div`
	position: relative;
	width: 100%;
	padding-top: ${getCssSizeValue(25, BREAKPOINTS.MOBILE)};
	margin-bottom: ${getCssSizeValue(100, BREAKPOINTS.MOBILE)};
	${underlineAnimationColorCss};
	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet || 768}px) {
		padding-top: ${getCssSizeValue(25, BREAKPOINTS.TABLET)};
		margin-bottom: ${getCssSizeValue(93, BREAKPOINTS.TABLET)};
	}
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		padding-top: ${getCssSizeValue(25, BREAKPOINTS.DESKTOP)};
		margin-bottom: ${getCssSizeValue(156, BREAKPOINTS.DESKTOP)};
	}
`;

const Heading = styled.div`
	color: var(--secondary-color);
	padding: ${getCssSizeValue(65, BREAKPOINTS.MOBILE)} 0 ${getCssSizeValue(80, BREAKPOINTS.MOBILE)};
	font-size: ${getFontSize(40, BREAKPOINTS.MOBILE)};
	line-height: ${getFontSize(57, BREAKPOINTS.MOBILE)};
	letter-spacing: -1.6px;
	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet || 768}px) {
		padding: ${getCssSizeValue(65, BREAKPOINTS.TABLET)} 0 ${getCssSizeValue(80, BREAKPOINTS.TABLET)};
		font-size: ${getFontSize(75, BREAKPOINTS.TABLET)};
		line-height: ${getFontSize(88, BREAKPOINTS.TABLET)};
		letter-spacing: -3px;
	}
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(100, BREAKPOINTS.DESKTOP)} 0;
		font-size: ${getFontSize(116, BREAKPOINTS.DESKTOP)};
		line-height: ${getFontSize(136, BREAKPOINTS.DESKTOP)};
		letter-spacing: -4.64px;
	}
`;

const BannerImage = styled.div`
	width: 100%;
	height: auto;
	position: relative;
	aspect-ratio: ${({ aspectRatio }) => (aspectRatio ? aspectRatio : 16 / 9)};
`;

const AllNewsButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	column-gap: 12px;
	padding-top: ${getCssSizeValue(220, BREAKPOINTS.DESKTOP)};

	svg {
		width: 16px;
		height: 8px;
	}

	div.icon-wrapper {
		display: flex;
		flex-direction: column;
		justify-content: center;
		width: 16px;
	}
`;

const AllArticlesLink = styled(Link)`
	font-size: ${getFontSize(14, BREAKPOINTS.MOBILE)};
	line-height: ${getFontSize(24, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet || 768}px) {
		font-size: ${getFontSize(14, BREAKPOINTS.TABLET)};
		line-height: ${getFontSize(24, BREAKPOINTS.TABLET)};
	}
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		font-size: ${getFontSize(18, BREAKPOINTS.DESKTOP)};
		line-height: ${getFontSize(24, BREAKPOINTS.DESKTOP)};
	}
`;

const InfoOverImageContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
`;

const CategoriesWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	margin-top: ${getCssSizeValue(19, BREAKPOINTS.MOBILE)};
	margin-bottom: ${getCssSizeValue(28, BREAKPOINTS.MOBILE)};
	column-gap: ${getCssSizeValue(13, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet || 768}px) {
		margin-top: ${getCssSizeValue(16, BREAKPOINTS.TABLET)};
		margin-bottom: ${getCssSizeValue(28, BREAKPOINTS.TABLET)};
		column-gap: ${getCssSizeValue(13, BREAKPOINTS.TABLET)};
	}
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) {
		margin-top: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
		margin-bottom: ${getCssSizeValue(28, BREAKPOINTS.DESKTOP)};
		column-gap: ${getCssSizeValue(15, BREAKPOINTS.DESKTOP)};
	}

	p.category {
		text-transform: uppercase;
		color: #a6a6a6;
		font-size: ${getFontSize(16, BREAKPOINTS.MOBILE)};
		line-height: ${getFontSize(24, BREAKPOINTS.MOBILE)};
		font-weight: ${({ theme }) => theme.fontWeight.regular};
		@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet}px) {
			font-size: ${getFontSize(16, BREAKPOINTS.TABLET)};
			line-height: ${getFontSize(24, BREAKPOINTS.TABLET)};
		}
		@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) {
			font-size: ${getFontSize(16, BREAKPOINTS.DESKTOP)};
			line-height: ${getFontSize(24, BREAKPOINTS.DESKTOP)};
		}
	}
`;

const SingleFiltrationTabWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding: 4px 13px;
	border-radius: 100px;
	cursor: pointer;
	border: 1px solid #051d27;
	color: #051d27;

	&.pleo-digital {
		border: 1px solid white;
		color: white;
		background-color: #e84f44;
	}

	&.pleo-design {
		border: 1px solid white;
		color: white;
		background-color: #2ac1d7;
	}

	&.pleo-method {
		border: 1px solid white;
		color: white;
		background-color: #9747ff;
	}

	&.pleo-group {
		border: 1px solid white;
		color: white;
		background-color: #1a303c;
	}
`;

const Text = styled.div`
	font-size: ${getFontSize(14, BREAKPOINTS.MOBILE)};
	line-height: normal;
	font-weight: ${({ theme }) => theme.fontWeight.semiBold};
	text-wrap: nowrap;
	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet}px) {
		font-size: ${getFontSize(14, BREAKPOINTS.TABLET)};
	}
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) {
		font-size: ${getFontSize(18, BREAKPOINTS.DESKTOP)};
	}
`;

const StyledReadTimeAndDateWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	margin-top: ${getCssSizeValue(7, BREAKPOINTS.MOBILE)};
	margin-bottom: ${getCssSizeValue(25, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet || 768}px) {
		margin-top: ${getCssSizeValue(7, BREAKPOINTS.TABLET)};
		margin-bottom: ${getCssSizeValue(25, BREAKPOINTS.TABLET)};
	}
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) {
		margin-top: ${getCssSizeValue(7, BREAKPOINTS.DESKTOP)};
		margin-bottom: ${getCssSizeValue(25, BREAKPOINTS.DESKTOP)};
	}

	p.read-time {
		font-size: ${getFontSize(14, BREAKPOINTS.MOBILE)};
		line-height: ${getFontSize(20, BREAKPOINTS.MOBILE)};
		@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet || 768}px) {
			font-size: ${getFontSize(14, BREAKPOINTS.TABLET)};
			line-height: ${getFontSize(54, BREAKPOINTS.TABLET)};
		}
		@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) {
			font-size: ${getFontSize(14, BREAKPOINTS.DESKTOP)};
			line-height: ${getFontSize(54, BREAKPOINTS.DESKTOP)};
		}
	}
`;
const sizes = generateSizes({ desktop: '90vw', tablet: '95vw', mobile: '93vw' });

const SingleNewsBanner = ({ data }) => {
	const { locale } = useRouter();
	const articleTitle = data?.blogPageName || '';
	const wordsCounter = data?.wordsCount?.words || 1;
	const wordsPerMinute = 200;
	const readingTimeInMinutes = Math.ceil(wordsCounter / wordsPerMinute) || 1;
	const assetHorizontalUrl = data?.thumbnailImageHorizontal?.url || '';
	const assetHorizontalWidth = data?.thumbnailImageHorizontal?.width || 0;
	const assetHorizontalHeight = data?.thumbnailImageHorizontal?.height || 1;
	const aspectHorizontalRatio =
		assetHorizontalWidth / assetHorizontalHeight === 0 ? '16/9' : assetHorizontalWidth / assetHorizontalHeight;
	const pleoCategory = data?.pleoCategory || '';
	const categories = data?.categories || [];
	const categoriesWithoutDashes = categories.map((item) => item.replace(/-/g, ' '));
	const backToAllNewsText = locale === 'pl' ? 'Powrót do wszystkich artykułów' : 'Back to all articles';
	const author = data?.author || {};
	const authorName = author?.fullNameSurname || '';
	const jobTitle = author?.jobTitle || '';
	const video = author?.video?.url || '';
	const createdAt = data?._createdAt || '';
	const readingTimeText = locale === 'pl' ? 'przeczytasz w' : 'read';
	const readTimeFullText =
		locale === 'pl' ? `${readingTimeText} ${readingTimeInMinutes} min` : `${readingTimeInMinutes} min ${readingTimeText}`;
	return (
		<SingleNewsBannerContainer className="px-main">
			<AllNewsButtonWrapper>
				<div className="icon-wrapper">
					<Icon name={ICONS.BUTTON_ARROW_LEFT} />
				</div>
				<AllArticlesLink href={`/news`} data-cursor={CURSOR_TYPES.LINK} className="underline-animation">
					{backToAllNewsText}
				</AllArticlesLink>
			</AllNewsButtonWrapper>
			<Heading>{articleTitle}</Heading>
			<InfoOverImageContainer>
				<CategoriesWrapper>
					<SingleFiltrationTabWrapper className={pleoCategory}>
						<Text>PLEO {pleoCategory.split('-')[1]}</Text>
					</SingleFiltrationTabWrapper>
					{categoriesWithoutDashes.map((item) => (
						<p className="category" key={item}>
							{item}
						</p>
					))}
				</CategoriesWrapper>
				{authorName && <AuthorInfo name={authorName} jobTitle={jobTitle} videoUrl={video} />}
				<StyledReadTimeAndDateWrapper>
					<p className="read-time">{readTimeFullText}</p>
					<DateInfo date={createdAt} locale={locale} />
				</StyledReadTimeAndDateWrapper>
			</InfoOverImageContainer>
			{assetHorizontalUrl && (
				<BannerImage aspectRatio={aspectHorizontalRatio}>
					<Image src={assetHorizontalUrl} sizes={sizes} />
				</BannerImage>
			)}
		</SingleNewsBannerContainer>
	);
};

export default SingleNewsBanner;
